import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes, faCalendarAlt, faClock, faTv,
  faGlobe, faInfoCircle, faPlay, faPlus,
  faStar, faCheck, faArrowLeft,
  faLayerGroup, faChevronLeft, faChevronRight
} from '@fortawesome/free-solid-svg-icons';
import { faStar as farStar } from '@fortawesome/free-regular-svg-icons';
import TrailerModal from './TrailerModal';
import ActorModal from './ActorModal';
import { Link } from 'react-router-dom';

// ============================================================
//  GIFT-CODE AD-FREE CHECK
// ============================================================
const REVOKED_CODES = [''];

const isUserAdFree = () => {
  const redeemedCode = localStorage.getItem('adFreeCode');
  if (!redeemedCode) return false;
  return !REVOKED_CODES.includes(redeemedCode);
};

// Language helpers
const languageMap = {
  en: 'English', ru: 'Russian', es: 'Spanish', fr: 'French', de: 'German',
  it: 'Italian', ja: 'Japanese', ko: 'Korean', zh: 'Chinese', hi: 'Hindi',
  pt: 'Portuguese', ar: 'Arabic', tr: 'Turkish', th: 'Thai', nl: 'Dutch',
  sv: 'Swedish', pl: 'Polish', da: 'Danish', no: 'Norwegian', fi: 'Finnish',
  el: 'Greek', cs: 'Czech', hu: 'Hungarian', he: 'Hebrew', id: 'Indonesian',
  ms: 'Malay', vi: 'Vietnamese', tl: 'Filipino', fa: 'Persian', bn: 'Bengali',
  ta: 'Tamil', te: 'Telugu', mr: 'Marathi', kn: 'Kannada', ml: 'Malayalam',
  ur: 'Urdu', uk: 'Ukrainian', ro: 'Romanian', sk: 'Slovak', bg: 'Bulgarian',
  hr: 'Croatian', sr: 'Serbian', sl: 'Slovenian', et: 'Estonian', lv: 'Latvian',
  lt: 'Lithuanian', ka: 'Georgian', hy: 'Armenian', sq: 'Albanian', mk: 'Macedonian',
};

const getLanguageName = (code) => languageMap[code] || code.toUpperCase();

const getTmdbLanguage = (lang) => {
  const map = { en: 'en-US', fr: 'fr-FR', es: 'es-ES', it: 'it-IT', nl: 'nl-NL' };
  return map[lang] || 'en-US';
};

// ================== TRANSLATIONS ==================
const translations = {
  en: {
    noOverview: "No overview available.",
    partOfCollection: "Part of a collection",
    collectionDescription: "These films may share a continuing storyline.",
    topCast: "Top Cast",
    supportingCast: "Supporting Cast",
    similarMovies: "Similar Movies",
    filmography: "Filmography",
    movies: "Movies",
    tvShows: "TV Shows",
    noFilmography: (type) => `No ${type} found with posters and at least 50 votes`,
    loadingFilmography: "Loading filmography...",
    unforgettable: "An unforgettable cinematic experience",
    votes: (count) => `(${count.toLocaleString()} votes)`,
    youAreHere: "You are here",
    back: "Back",
    loadingSeries: "Loading series details...",
    seasonsEpisodes: "Seasons & Episodes",
    selectSeason: "Select Season:",
    seasonLabel: "Season",
    episodesOf: (current, total) => `Episodes (${current} of ${total})`,
    episodeNotReleased: "Episode not released yet",
    lastWatched: "Last Watched",
    noDescription: "No description available.",
    loadMoreEpisodes: (remaining) => `Load More Episodes (${remaining} remaining)`,
    seasonNotReleased: "Season not released yet",
  },
  fr: {
    noOverview: "Aucun aperçu disponible.",
    partOfCollection: "Fait partie d'une collection",
    collectionDescription: "Ces films peuvent partager une intrigue continue.",
    topCast: "Distribution principale",
    supportingCast: "Distribution secondaire",
    similarMovies: "Films similaires",
    filmography: "Filmographie",
    movies: "Films",
    tvShows: "Séries TV",
    noFilmography: (type) => `Aucun ${type} trouvé avec des affiches et au moins 50 votes`,
    loadingFilmography: "Chargement de la filmographie...",
    unforgettable: "Une expérience cinématographique inoubliable",
    votes: (count) => `(${count.toLocaleString()} votes)`,
    youAreHere: "Vous êtes ici",
    back: "Retour",
    loadingSeries: "Chargement des détails de la série...",
    seasonsEpisodes: "Saisons et épisodes",
    selectSeason: "Sélectionner la saison :",
    seasonLabel: "Saison",
    episodesOf: (current, total) => `Épisodes (${current} sur ${total})`,
    episodeNotReleased: "Épisode non encore diffusé",
    lastWatched: "Dernier visionnage",
    noDescription: "Aucune description disponible.",
    loadMoreEpisodes: (remaining) => `Charger plus d'épisodes (${remaining} restants)`,
    seasonNotReleased: "Saison non encore diffusée",
  },
  es: {
    noOverview: "No hay resumen disponible.",
    partOfCollection: "Parte de una colección",
    collectionDescription: "Estas películas pueden compartir una historia continua.",
    topCast: "Reparto principal",
    supportingCast: "Reparto secundario",
    similarMovies: "Películas similares",
    filmography: "Filmografía",
    movies: "Películas",
    tvShows: "Series TV",
    noFilmography: (type) => `No se encontraron ${type} con pósteres y al menos 50 votos`,
    loadingFilmography: "Cargando filmografía...",
    unforgettable: "Una experiencia cinematográfica inolvidable",
    votes: (count) => `(${count.toLocaleString()} votos)`,
    youAreHere: "Estás aquí",
    back: "Volver",
    loadingSeries: "Cargando detalles de la serie...",
    seasonsEpisodes: "Temporadas y episodios",
    selectSeason: "Seleccionar temporada:",
    seasonLabel: "Temporada",
    episodesOf: (current, total) => `Episodios (${current} de ${total})`,
    episodeNotReleased: "Episodio no emitido aún",
    lastWatched: "Último visto",
    noDescription: "Sin descripción disponible.",
    loadMoreEpisodes: (remaining) => `Cargar más episodios (${remaining} restantes)`,
    seasonNotReleased: "Temporada no emitida aún",
  },
  it: {
    noOverview: "Nessuna panoramica disponibile.",
    partOfCollection: "Parte di una collezione",
    collectionDescription: "Questi film potrebbero condividere una trama continua.",
    topCast: "Cast principale",
    supportingCast: "Cast di supporto",
    similarMovies: "Film simili",
    filmography: "Filmografia",
    movies: "Film",
    tvShows: "Serie TV",
    noFilmography: (type) => `Nessun ${type} trovato con poster e almeno 50 voti`,
    loadingFilmography: "Caricamento filmografia...",
    unforgettable: "Un'esperienza cinematografica indimenticabile",
    votes: (count) => `(${count.toLocaleString()} voti)`,
    youAreHere: "Sei qui",
    back: "Indietro",
    loadingSeries: "Caricamento dettagli della serie...",
    seasonsEpisodes: "Stagioni ed episodi",
    selectSeason: "Seleziona stagione:",
    seasonLabel: "Stagione",
    episodesOf: (current, total) => `Episodi (${current} di ${total})`,
    episodeNotReleased: "Episodio non ancora trasmesso",
    lastWatched: "Ultimo visto",
    noDescription: "Nessuna descrizione disponibile.",
    loadMoreEpisodes: (remaining) => `Carica altri episodi (${remaining} rimanenti)`,
    seasonNotReleased: "Stagione non ancora trasmessa",
  },
  nl: {
    noOverview: "Geen overzicht beschikbaar.",
    partOfCollection: "Onderdeel van een collectie",
    collectionDescription: "Deze films kunnen een doorlopend verhaal delen.",
    topCast: "Hoofdcast",
    supportingCast: "Ondersteunende cast",
    similarMovies: "Soortgelijke films",
    filmography: "Filmografie",
    movies: "Films",
    tvShows: "TV-series",
    noFilmography: (type) => `Geen ${type} gevonden met posters en minimaal 50 stemmen`,
    loadingFilmography: "Filmografie laden...",
    unforgettable: "Een onvergetelijke filmervaring",
    votes: (count) => `(${count.toLocaleString()} stemmen)`,
    youAreHere: "Je bent hier",
    back: "Terug",
    loadingSeries: "Seriegegevens laden...",
    seasonsEpisodes: "Seizoenen en afleveringen",
    selectSeason: "Selecteer seizoen:",
    seasonLabel: "Seizoen",
    episodesOf: (current, total) => `Afleveringen (${current} van ${total})`,
    episodeNotReleased: "Aflevering nog niet uitgezonden",
    lastWatched: "Laatst bekeken",
    noDescription: "Geen beschrijving beschikbaar.",
    loadMoreEpisodes: (remaining) => `Meer afleveringen laden (${remaining} resterend)`,
    seasonNotReleased: "Seizoen nog niet uitgezonden",
  },
};

// Star rating helper
const createStarRating = (rating, size = 'normal') => {
  const starRating = rating / 2;
  const fullStars = Math.floor(starRating);
  const partialStar = starRating - fullStars;
  const starSize = size === 'small' ? '14px' : '19px';
  const stars = [];

  for (let i = 0; i < fullStars; i++) {
    stars.push(
      <div className="star" key={`full-${i}`} style={{ fontSize: starSize }}>
        <FontAwesomeIcon icon={farStar} />
        <div className="star-filled" style={{ width: '100%' }}>
          <FontAwesomeIcon icon={faStar} />
        </div>
      </div>
    );
  }
  if (partialStar > 0) {
    stars.push(
      <div className="star" key="partial" style={{ fontSize: starSize }}>
        <FontAwesomeIcon icon={farStar} />
        <div className="star-filled" style={{ width: `${partialStar * 100}%` }}>
          <FontAwesomeIcon icon={faStar} />
        </div>
      </div>
    );
  }
  const emptyStars = 5 - Math.ceil(starRating);
  for (let i = 0; i < emptyStars; i++) {
    stars.push(
      <div className="star" key={`empty-${i}`} style={{ fontSize: starSize }}>
        <FontAwesomeIcon icon={farStar} />
        <div className="star-filled" style={{ width: '0%' }}>
          <FontAwesomeIcon icon={faStar} />
        </div>
      </div>
    );
  }
  return stars;
};

// ================== MAIN COMPONENT ==================
const MediaModal = ({
  media,
  mediaType: initialType = 'movie',
  onClose,
  onUpdate,
  onBack,
  onMediaChange,
  history
}) => {
  // ----- STATE -----
  const [currentMedia, setCurrentMedia] = useState(media);
  const [type, setType] = useState(initialType);
  const [credits, setCredits] = useState(null);
  const [videos, setVideos] = useState(null);
  const [trailerKey, setTrailerKey] = useState(null);
  const [isAdded, setIsAdded] = useState(false);
  const [recommendedMovies, setRecommendedMovies] = useState([]);
  const [collection, setCollection] = useState(null);
  const [showCollection, setShowCollection] = useState(false);
  const [collectionScrollIndex, setCollectionScrollIndex] = useState(0);
  const [showBlinkingEffect, setShowBlinkingEffect] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Series-specific
  const [seasonData, setSeasonData] = useState(null);
  const [selectedSeason, setSelectedSeason] = useState(1);
  const [episodePage, setEpisodePage] = useState(1);
  const [lastWatchedEpisode, setLastWatchedEpisode] = useState(null);
  const [selectedEpisodeId, setSelectedEpisodeId] = useState(null);
  const [showEpisodeActions, setShowEpisodeActions] = useState(false);

  // Actor modal
  const [selectedActor, setSelectedActor] = useState(null);

  // Navigation history
  const [localHistory, setLocalHistory] = useState(history || []);
  const [showTrailerModal, setShowTrailerModal] = useState(false);

  // Language & rate limit
  const [language, setLanguage] = useState(() => localStorage.getItem('preferredLanguage') || 'en');
  const [isRateLimited, setIsRateLimited] = useState(false);
  const [cooldownSeconds, setCooldownSeconds] = useState(0);

  // Refs
  const modalContentRef = useRef(null);
  const isInitialMount = useRef(true);
  const cooldownIntervalRef = useRef(null);
  const requestTimestampsRef = useRef([]);
  const blinkIntervalRef = useRef(null);
  const lastScrolledMediaIdRef = useRef(null);
  const lastScrolledActorIdRef = useRef(null);

  // API key & ad link
  const apiKey = '57240db50c2008e78c261e1a934627e4';
  const adLink = 'https://amrox.org/4/0eb5c90380ccc36ce2bdf72e653ec6bb';
  const actorFallbackImage = 'https://i.ibb.co/Lkf9x3W/hsaSDasa.png';
  const episodesPerPage = 25;

  // Translation helper
  const t = (key, ...args) => {
    const langData = translations[language] || translations.en;
    const keys = key.split('.');
    let value = langData;
    for (const k of keys) {
      if (value && value[k] !== undefined) {
        value = value[k];
      } else {
        const enData = translations.en;
        let fallback = enData;
        for (const fk of keys) {
          if (fallback && fallback[fk] !== undefined) {
            fallback = fallback[fk];
          } else {
            return key;
          }
        }
        if (typeof fallback === 'function') return fallback(...args);
        return fallback;
      }
    }
    if (typeof value === 'function') return value(...args);
    return value;
  };

  // Format actor name for mobile
  const formatActorName = (name) => {
    if (window.innerWidth > 768) return name;
    const parts = name.split(' ');
    if (parts.length === 1) return name.length > 16 ? name.substring(0, 16) + '.' : name;
    const firstName = parts[0];
    const lastName = parts[parts.length - 1];
    const truncatedFirstName = firstName.length > 8 ? firstName.substring(0, 8) + '.' : firstName;
    const truncatedLastName = lastName.length > 8 ? lastName.substring(0, 8) + '.' : lastName;
    return `${truncatedFirstName} ${truncatedLastName}`;
  };

  // ----- SCROLL TO TOP (delayed) -----
  const scrollToTopDelayed = (delay = 150) => {
    setTimeout(() => {
      if (modalContentRef.current) {
        modalContentRef.current.scrollTop = 0;
      }
    }, delay);
  };

  // ----- RATE LIMITING -----
  const checkRateLimit = () => {
    if (isRateLimited) return false;
    const now = Date.now();
    const tenSecondsAgo = now - 10000;
    const recent = requestTimestampsRef.current.filter(ts => ts > tenSecondsAgo);
    if (recent.length >= 25) {
      setIsRateLimited(true);
      setCooldownSeconds(8);
      cooldownIntervalRef.current = setInterval(() => {
        setCooldownSeconds(prev => {
          if (prev <= 1) {
            clearInterval(cooldownIntervalRef.current);
            setIsRateLimited(false);
            requestTimestampsRef.current = [];
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return false;
    }
    requestTimestampsRef.current.push(now);
    return true;
  };

  // ----- FETCHING FUNCTIONS -----
  const fetchMediaDetails = async (id, lang) => {
    const tmdbLang = getTmdbLanguage(lang);
    const [enRes, userRes] = await Promise.all([
      fetch(`https://api.themoviedb.org/3/${type}/${id}?api_key=${apiKey}&language=en-US`),
      fetch(`https://api.themoviedb.org/3/${type}/${id}?api_key=${apiKey}&language=${tmdbLang}`)
    ]);
    const enData = await enRes.json();
    const userData = await userRes.json();
    const merged = {
      ...userData,
      title: enData.title || enData.name,
      original_title: enData.original_title || enData.original_name || merged.title,
      name: enData.name || enData.title,
    };
    return merged;
  };

  const fetchCredits = async (id, lang) => {
    const tmdbLang = getTmdbLanguage(lang);
    const res = await fetch(`https://api.themoviedb.org/3/${type}/${id}/credits?api_key=${apiKey}&language=${tmdbLang}`);
    return res.json();
  };

  const fetchVideos = async (id) => {
    const res = await fetch(`https://api.themoviedb.org/3/${type}/${id}/videos?api_key=${apiKey}&language=en-US`);
    return res.json();
  };

  const fetchRecommendations = async (id, lang) => {
    if (type !== 'movie') return [];
    const tmdbLang = getTmdbLanguage(lang);
    const res = await fetch(`https://api.themoviedb.org/3/movie/${id}/recommendations?api_key=${apiKey}&language=${tmdbLang}&page=1`);
    const data = await res.json();
    return data.results || [];
  };

  const fetchCollection = async (collectionId, lang) => {
    if (type !== 'movie') return null;
    const tmdbLang = getTmdbLanguage(lang);
    const res = await fetch(`https://api.themoviedb.org/3/collection/${collectionId}?api_key=${apiKey}&language=${tmdbLang}`);
    const data = await res.json();
    const valid = data.parts?.filter(m => m.poster_path && m.vote_count > 0) || [];
    return valid.length > 1 ? { ...data, parts: valid } : null;
  };

  const fetchSeasonDetails = async (seriesId, seasonNumber, lang) => {
    const tmdbLang = getTmdbLanguage(lang);
    const res = await fetch(`https://api.themoviedb.org/3/tv/${seriesId}/season/${seasonNumber}?api_key=${apiKey}&language=${tmdbLang}`);
    return res.json();
  };

  // ----- MAIN DATA LOADER -----
  const loadMediaData = async (mediaId, mediaType, lang) => {
    if (!mediaId) return;
    if (!checkRateLimit()) return;

    setIsLoading(true);
    try {
      const [details, creditsData, videosData] = await Promise.all([
        fetchMediaDetails(mediaId, lang),
        fetchCredits(mediaId, lang),
        fetchVideos(mediaId)
      ]);

      setCurrentMedia(details);
      setCredits(creditsData);
      setVideos(videosData);

      const trailer = videosData.results?.find(v => v.type === 'Trailer');
      setTrailerKey(trailer ? trailer.key : null);

      const watchlist = JSON.parse(localStorage.getItem('watchlist')) || [];
      const alreadyAdded = watchlist.some(item => item.id === details.id && item.type === mediaType);
      setIsAdded(alreadyAdded);

      if (mediaType === 'movie') {
        const [recs, collectionData] = await Promise.all([
          fetchRecommendations(mediaId, lang),
          details.belongs_to_collection ? fetchCollection(details.belongs_to_collection.id, lang) : null
        ]);
        setRecommendedMovies(recs);
        setCollection(collectionData);
        setShowCollection(false);
        setCollectionScrollIndex(0);
      } else {
        setRecommendedMovies([]);
        setCollection(null);
        const lastWatchedKey = `lastWatched_${mediaId}`;
        const stored = localStorage.getItem(lastWatchedKey);
        let initialSeason = 1;
        let storedEpisodeId = null;
        if (stored) {
          const parsed = JSON.parse(stored);
          if (parsed.season >= 1 && parsed.season <= details.number_of_seasons) {
            initialSeason = parsed.season;
            storedEpisodeId = parsed.episodeId;
          }
        }
        setSelectedSeason(initialSeason);
        if (details.number_of_seasons > 0) {
          const season = await fetchSeasonDetails(mediaId, initialSeason, lang);
          setSeasonData(season);
          if (storedEpisodeId && season.episodes.some(ep => ep.id === storedEpisodeId)) {
            setLastWatchedEpisode(storedEpisodeId);
          }
        } else {
          setSeasonData(null);
        }
        setEpisodePage(1);
      }

      setIsLoading(false);
    } catch (error) {
      console.error('Error loading media data:', error);
      setIsLoading(false);
    }
  };

  // Trigger load when media or type or language changes
  useEffect(() => {
    if (currentMedia && currentMedia.id) {
      loadMediaData(currentMedia.id, type, language);
    }
  }, [currentMedia?.id, type, language]);

  // Handle initial load when component mounts (if media provided)
  useEffect(() => {
    if (media) {
      setCurrentMedia(media);
      setType(initialType);
    }
  }, [media, initialType]);

  // Listen for language changes from other tabs
  useEffect(() => {
    const handleStorage = (e) => {
      if (e.key === 'preferredLanguage') {
        const newLang = e.newValue || 'en';
        setLanguage(newLang);
        if (currentMedia && currentMedia.id) {
          loadMediaData(currentMedia.id, type, newLang);
        }
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [currentMedia, type]);

  // Collection blinking effect
  useEffect(() => {
    if (collection && showBlinkingEffect) {
      blinkIntervalRef.current = setInterval(() => {
        const el = document.querySelector('.collection-section.capsule');
        if (el) el.classList.toggle('blinking');
      }, 1000);
    }
    return () => {
      if (blinkIntervalRef.current) clearInterval(blinkIntervalRef.current);
    };
  }, [collection, showBlinkingEffect]);

  // Scroll when media data is fully loaded
  useEffect(() => {
    if (!isLoading && currentMedia && currentMedia.id) {
      const mediaId = currentMedia.id;
      if (lastScrolledMediaIdRef.current !== mediaId) {
        scrollToTopDelayed(150);
        lastScrolledMediaIdRef.current = mediaId;
      }
    }
  }, [isLoading, currentMedia, credits, recommendedMovies, seasonData]);

  // Body overflow
  useEffect(() => {
    if (currentMedia) {
      document.body.style.overflow = 'hidden';
      const event = new CustomEvent('mediaOpened', { detail: { ...currentMedia, media_type: type } });
      window.dispatchEvent(event);
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => { document.body.style.overflow = 'auto'; };
  }, [currentMedia, type]);

  // Cleanup intervals
  useEffect(() => {
    return () => {
      if (cooldownIntervalRef.current) clearInterval(cooldownIntervalRef.current);
      if (blinkIntervalRef.current) clearInterval(blinkIntervalRef.current);
    };
  }, []);

  // ----- HANDLERS -----
  const handleAddToWatchlist = () => {
    if (!currentMedia) return;
    const watchlist = JSON.parse(localStorage.getItem('watchlist')) || [];
    const alreadyAdded = watchlist.some(item => item.id === currentMedia.id && item.type === type);
    let updated;
    if (alreadyAdded) {
      updated = watchlist.filter(item => !(item.id === currentMedia.id && item.type === type));
    } else {
      const data = {
        id: currentMedia.id,
        title: type === 'movie' ? currentMedia.title : currentMedia.name,
        poster_path: currentMedia.poster_path,
        release_date: type === 'movie' ? currentMedia.release_date : currentMedia.first_air_date,
        vote_average: currentMedia.vote_average,
        type: type
      };
      updated = [...watchlist, data];
    }
    localStorage.setItem('watchlist', JSON.stringify(updated));
    setIsAdded(!alreadyAdded);
    if (onUpdate) onUpdate();
  };

  const handleBack = () => {
    if (onBack && localHistory.length === 0) {
      onBack();
    } else if (localHistory.length > 0) {
      const prev = localHistory[localHistory.length - 1];
      if (prev.type === 'movie-from-actor' || prev.type === 'movie' || prev.type === 'movie-from-collection') {
        setCurrentMedia(prev.movie);
        setType('movie');
        setSelectedActor(prev.actor || null);
      } else if (prev.type === 'actor') {
        setSelectedActor(prev.actor);
      } else if (prev.type === 'tv-show-from-actor') {
        setSelectedActor(prev.actor);
        setSelectedActor(null);
      }
      setLocalHistory(prev => prev.slice(0, -1));
    }
  };

  const handleActorClick = (person) => {
    if (isRateLimited || !checkRateLimit()) return;
    setLocalHistory(prev => [...prev, { type: 'actor', movie: currentMedia, actor: selectedActor }]);
    setSelectedActor(person);
  };

  const handleCloseActorModal = () => {
    setSelectedActor(null);
    if (localHistory.length > 0) {
      const prev = localHistory[localHistory.length - 1];
      if (prev.type === 'actor') {
        setLocalHistory(prev => prev.slice(0, -1));
      }
    }
  };

  // ---- Media change handler (notifies parent) ----
  const changeMedia = (item, itemType) => {
    setCurrentMedia(item);
    setType(itemType);
    setSelectedActor(null);
    lastScrolledMediaIdRef.current = null;
    if (onMediaChange) {
      onMediaChange(item, itemType);
    }
  };

  const handleMediaClick = (item, itemType) => {
    if (isRateLimited || !checkRateLimit()) return;
    setLocalHistory(prev => [...prev, {
      type: itemType === 'movie' ? 'movie' : 'tv-show',
      movie: currentMedia,
      actor: selectedActor
    }]);
    changeMedia(item, itemType);
  };

  // This will be passed to ActorModal's onContentClick
  const handleFilmographyItemClick = (item, itemType) => {
    if (isRateLimited || !checkRateLimit()) return;
    if (itemType === 'movie') {
      setLocalHistory(prev => [...prev, { type: 'movie-from-actor', movie: currentMedia, actor: selectedActor }]);
      changeMedia(item, 'movie');
    } else if (itemType === 'tv') {
      setLocalHistory(prev => [...prev, { type: 'tv-show-from-actor', movie: currentMedia, actor: selectedActor }]);
      changeMedia(item, 'tv');
    }
    setSelectedActor(null);
  };

  const handleSeasonChange = async (seasonNumber) => {
    setSelectedSeason(seasonNumber);
    setEpisodePage(1);
    setSelectedEpisodeId(null);
    setShowEpisodeActions(false);
    if (currentMedia && currentMedia.id) {
      try {
        const season = await fetchSeasonDetails(currentMedia.id, seasonNumber, language);
        setSeasonData(season);
        const lastWatchedKey = `lastWatched_${currentMedia.id}`;
        const stored = localStorage.getItem(lastWatchedKey);
        if (stored) {
          const parsed = JSON.parse(stored);
          if (parsed.season === seasonNumber) {
            const exists = season.episodes.some(ep => ep.id === parsed.episodeId);
            if (exists) setLastWatchedEpisode(parsed.episodeId);
          }
        }
      } catch (error) {
        console.error('Error fetching season:', error);
      }
    }
  };

  const handleEpisodeClick = (episodeId) => {
    if (selectedEpisodeId === episodeId) {
      setSelectedEpisodeId(null);
      setShowEpisodeActions(false);
    } else {
      setSelectedEpisodeId(episodeId);
      setShowEpisodeActions(true);
    }
  };

  const handleActionClick = (episodeId) => {
    const key = `lastWatched_${currentMedia.id}`;
    localStorage.setItem(key, JSON.stringify({ season: selectedSeason, episodeId }));
    setLastWatchedEpisode(episodeId);
  };

  const handlePlayClick = () => {
    if (!isUserAdFree()) window.open(adLink, '_blank');
  };

  const handleCollectionClick = () => {
    localStorage.setItem('collectionCapsuleClicked', 'true');
    setShowBlinkingEffect(false);
    setShowCollection(true);
    if (blinkIntervalRef.current) clearInterval(blinkIntervalRef.current);
  };

  const scrollCollection = (direction) => {
    if (!collection) return;
    const itemsPerPage = window.innerWidth <= 768 ? 3 : 5;
    const maxIndex = Math.ceil(collection.parts.length / itemsPerPage) - 1;
    if (direction === 'next') {
      setCollectionScrollIndex(prev => Math.min(prev + 1, maxIndex));
    } else {
      setCollectionScrollIndex(prev => Math.max(prev - 1, 0));
    }
  };

  // ----- RENDER HELPERS -----
  // Renders the action buttons (trailer + add to watchlist) and optionally the play links
  const renderActionButtons = (showPlayButtons) => {
    return (
      <div className="modal-actions-container">
        <div className="modal-actions">
          <button className={`action-btn trailer-btn ${!trailerKey ? 'disabled' : ''}`} onClick={() => trailerKey && setShowTrailerModal(true)} disabled={!trailerKey}>
            <FontAwesomeIcon icon={faPlay} />
            {trailerKey ? 'Watch Trailer' : 'Trailer Not Available'}
          </button>
          <button className={`action-btn add-btn ${isAdded ? 'added' : ''}`} onClick={handleAddToWatchlist}>
            <FontAwesomeIcon icon={isAdded ? faCheck : faPlus} />
            {isAdded ? 'In Your List' : 'Add to Watchlist'}
          </button>
        </div>
        {showPlayButtons && (
          <div className="modal-actions-extra">
            <Link className="action-btn action-btn-pink btn-play" to={`/watch/${currentMedia.id}`} onClick={handlePlayClick}>
              <FontAwesomeIcon icon={faPlay} /><span>Play</span>
            </Link>
            <Link className="action-btn action-btn-gray btn-play" to={`/alt-watch/${currentMedia.id}`} onClick={handlePlayClick}>
              <FontAwesomeIcon icon={faPlay} /><span>Play 2</span>
            </Link>
            <Link className="action-btn action-btn-gray btn-play" to={`/alt-watch-two/${currentMedia.id}`} onClick={handlePlayClick}>
              <FontAwesomeIcon icon={faPlay} /><span>Play 3</span>
            </Link>
          </div>
        )}
      </div>
    );
  };

  const renderCast = (castList, title, limit) => {
    if (!castList || castList.length === 0) return null;
    const isMobile = window.innerWidth <= 768;
    const count = isMobile ? 4 : 5;
    const slice = castList.slice(0, limit || count);
    return (
      <>
        <h3 className="cast-title">{title}</h3>
        <div className="cast-grid" style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${isMobile ? 4 : 5}, 1fr)`,
          gap: '15px',
          marginBottom: '30px'
        }}>
          {slice.map(person => {
            const photo = person.profile_path ? `https://image.tmdb.org/t/p/w200${person.profile_path}` : actorFallbackImage;
            return (
              <div key={person.credit_id} className="cast-member" onClick={() => handleActorClick(person)} style={{ cursor: 'pointer', textAlign: 'center' }}>
                <div style={{ position: 'relative', width: isMobile ? '70px' : '100px', height: isMobile ? '70px' : '100px', borderRadius: '50%', overflow: 'hidden', margin: '0 auto 8px', border: '2px solid rgba(255,255,255,0.1)' }}>
                  <img src={photo} alt={person.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={(e) => { e.target.onerror = null; e.target.src = actorFallbackImage; }} />
                </div>
                <div className="cast-name" style={{ fontSize: isMobile ? '0.8rem' : '0.9rem', fontWeight: 'bold', marginBottom: '4px' }}>{formatActorName(person.name)}</div>
                <div className="cast-character" style={{ fontSize: isMobile ? '0.7rem' : '0.8rem', color: '#aaa' }}>{person.character}</div>
              </div>
            );
          })}
        </div>
      </>
    );
  };

  // If no media, render nothing
  if (!currentMedia) return null;

  const isMovie = type === 'movie';
  const title = isMovie ? currentMedia.title : currentMedia.name;
  const posterPath = currentMedia.poster_path ? `https://image.tmdb.org/t/p/w500${currentMedia.poster_path}` : 'https://via.placeholder.com/500x750?text=No+Poster';
  const backdropPath = currentMedia.backdrop_path ? `https://image.tmdb.org/t/p/w780${currentMedia.backdrop_path}` : 'https://via.placeholder.com/1920x1080?text=No+Backdrop';
  const voteAvg = currentMedia.vote_average ? currentMedia.vote_average.toFixed(1) : 'N/A';
  const voteCount = currentMedia.vote_count || 0;

  // Meta data
  let year, runtime, seasons, status, languageCode;
  if (isMovie) {
    year = currentMedia.release_date ? new Date(currentMedia.release_date).getFullYear() : 'N/A';
    runtime = currentMedia.runtime || 0;
    status = currentMedia.status || 'Released';
    languageCode = currentMedia.original_language;
  } else {
    year = currentMedia.first_air_date ? new Date(currentMedia.first_air_date).getFullYear() : 'N/A';
    seasons = currentMedia.number_of_seasons || 0;
    status = currentMedia.status || 'Returning Series';
    languageCode = currentMedia.original_language;
  }

  const runtimeStr = runtime ? `${runtime} min` : '';
  const seasonsStr = seasons ? `${seasons} season${seasons > 1 ? 's' : ''}` : '';

  // Episode data
  const episodes = seasonData?.episodes || [];
  const totalEpisodes = episodes.length;
  const currentEpisodes = episodes.slice(0, episodePage * episodesPerPage);
  const hasMoreEpisodes = totalEpisodes > currentEpisodes.length;

  const topCastCount = window.innerWidth <= 768 ? 4 : 5;
  const supportingCastCount = window.innerWidth <= 768 ? 4 : 5;
  const recCount = window.innerWidth <= 768 ? 6 : 10;
  const recMovies = recommendedMovies.slice(0, recCount);

  // Collection carousel
  const itemsPerPage = window.innerWidth <= 768 ? 3 : 5;
  const collectionItems = collection ? collection.parts.sort((a, b) => new Date(a.release_date) - new Date(b.release_date)) : [];
  const maxScrollIndex = collection ? Math.ceil(collectionItems.length / itemsPerPage) - 1 : 0;
  const canScrollLeft = collectionScrollIndex > 0;
  const canScrollRight = collectionScrollIndex < maxScrollIndex;

  // Predefined backdrops for actor modal (can be any images)
  const predefinedBackdrops = [
    'https://image.tmdb.org/t/p/w1280/5dXHhN1ZmBTh7H4gQhZj1ZDQ6IF.jpg',
    'https://image.tmdb.org/t/p/w1280/7m2p4sZ8b9L5g2n0Q3Yt1Vw7FjL.jpg',
    'https://image.tmdb.org/t/p/w1280/9U2P3wT6j5kDl1QnZ8hXv4fR6t.jpg',
    'https://image.tmdb.org/t/p/w1280/6rL8Gq2mQjF4sT9pN3oX1wB5E7.jpg',
  ];

  const getHighResImage = (url) => {
    if (!url) return null;
    return url.replace('original', 'w500');
  };

  return (
    <div className="movie-modal active" onClick={(e) => e.target.classList.contains('movie-modal') && onClose()}>
      <style>
        {`
          @keyframes blink {
            0% { box-shadow: 0 0 0 0 rgba(30, 255, 0, 0.78); }
            50% { box-shadow: 0 0 0 10px rgba(229, 9, 20, 0); }
            100% { box-shadow: 0 0 0 0 rgba(229, 9, 20, 0); }
          }
          .blinking { animation: blink 2s infinite; }
        `}
      </style>

      {/* Rate Limit Overlay */}
      {isRateLimited && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, flexDirection: 'column', color: 'white' }}>
          <div style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1rem' }}>Please slow down</div>
          <div style={{ fontSize: '3rem', fontWeight: 'bold', color: '#e50914' }}>{cooldownSeconds}s</div>
          <p style={{ marginTop: '1rem', textAlign: 'center', maxWidth: '300px' }}>You've been making requests too quickly. Please wait a moment.</p>
        </div>
      )}

      <div className="modal-backdrop" style={{ backgroundImage: `url(${backdropPath})` }}></div>
      <div className="modal-overlay"></div>
      <div className="modal-content" ref={modalContentRef} style={{ overflowY: 'auto' }}>
        <div className="modal-header">
          {(localHistory.length > 0 || onBack) && (
            <div className="back-modal" onClick={handleBack} style={{
              position: 'absolute', top: '30px', right: '90px',
              background: 'rgba(255,255,255,0.15)', width: '50px', height: '50px',
              borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', transition: 'all 0.3s ease', zIndex: 10,
              border: '1px solid rgba(255,255,255,0.1)'
            }}>
              <FontAwesomeIcon icon={faArrowLeft} style={{ color: 'white', fontSize: '24px' }} />
            </div>
          )}
          <div className="close-modal" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </div>
        </div>
        <div className="modal-body">
          {isLoading ? (
            <div className="modal-loading" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '300px', width: '100%' }}>
              <div className="spinner"></div>
              <p>{isMovie ? 'Loading movie details...' : t('loadingSeries')}</p>
            </div>
          ) : (
            <>
              <div className="modal-poster">
                <img src={posterPath} alt={title} style={{ width: '100%', height: 'auto', aspectRatio: '2/3', objectFit: 'cover', borderRadius: '8px' }} />
              </div>
              <div className="modal-details">
                <h1 className="modal-title">{title}</h1>
                <p className="modal-tagline">{currentMedia.tagline || t('unforgettable')}</p>

                <div className="modal-meta">
                  <div className="meta-item">
                    <FontAwesomeIcon icon={faCalendarAlt} />
                    <span>{year}</span>
                  </div>
                  {isMovie && runtime > 0 && (
                    <div className="meta-item">
                      <FontAwesomeIcon icon={faClock} />
                      <span>{runtimeStr}</span>
                    </div>
                  )}
                  {!isMovie && seasons > 0 && (
                    <div className="meta-item">
                      <FontAwesomeIcon icon={faTv} />
                      <span>{seasonsStr}</span>
                    </div>
                  )}
                  <div className="meta-item">
                    <FontAwesomeIcon icon={faGlobe} />
                    <span>{getLanguageName(languageCode)}</span>
                  </div>
                  {status && (
                    <div className="meta-item">
                      <FontAwesomeIcon icon={faInfoCircle} />
                      <span>{status}</span>
                    </div>
                  )}
                </div>

                <div className="modal-rating">
                  <div className="rating-value">{voteAvg}</div>
                  <div className="rating-count">{t('votes', voteCount)}</div>
                </div>

                <div className="modal-genres">
                  {currentMedia.genres?.map(genre => (
                    <div key={genre.id} className="genre">{genre.name}</div>
                  ))}
                </div>

                <p className="modal-overview">{currentMedia.overview || t('noOverview')}</p>

                {/* Collection (movies only) */}
                {isMovie && collection && !showCollection && (
                  <div className={`collection-section capsule ${showBlinkingEffect ? 'blinking' : ''}`} onClick={handleCollectionClick} style={{
                    marginBottom: '20px', padding: '15px', background: 'rgba(255,255,255,0.05)',
                    borderRadius: '10px', border: '1px solid rgba(255,255,255,0.2)',
                    cursor: 'pointer', transition: 'all 0.3s ease'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <FontAwesomeIcon icon={faLayerGroup} style={{ marginRight: '10px', color: 'rgb(53, 229, 9)' }} />
                      <h3 style={{ margin: 0, fontSize: '1rem' }}>{t('partOfCollection')}</h3>
                    </div>
                  </div>
                )}
                {isMovie && collection && showCollection && (
                  <div className="collection-carousel" style={{ marginBottom: '30px', borderRadius: '8px', position: 'relative' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '15px' }}>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <FontAwesomeIcon icon={faLayerGroup} style={{ marginRight: '10px', color: 'rgb(53, 229, 9)' }} />
                        <h3 style={{ margin: 0 }}>{collection.name}</h3>
                      </div>
                    </div>
                    <div style={{ fontSize: '0.9rem', fontStyle: 'italic', color: 'rgba(255,255,255,0.7)', marginBottom: '20px', marginTop: '-15px' }}>
                      "{t('collectionDescription')}"
                    </div>
                    <div className="collection-carousel-container" style={{ position: 'relative' }}>
                      {canScrollLeft && (
                        <button className="carousel-arrow left" onClick={() => scrollCollection('prev')} style={{
                          position: 'absolute', left: '-20px', top: window.innerWidth <= 768 ? '80px' : '110px',
                          transform: 'translateY(-50%)', background: 'rgba(0,0,0,0.7)', border: 'none',
                          borderRadius: '50%', width: '40px', height: '40px', color: 'white',
                          cursor: 'pointer', zIndex: 5, display: 'flex', alignItems: 'center', justifyContent: 'center'
                        }}>
                          <FontAwesomeIcon icon={faChevronLeft} />
                        </button>
                      )}
                      <div className="collection-items" style={{
                        display: 'grid',
                        gridTemplateColumns: `repeat(${itemsPerPage}, 1fr)`,
                        gap: '15px', overflow: 'hidden'
                      }}>
                        {collectionItems.map((movie, index) => {
                          const visible = index >= collectionScrollIndex * itemsPerPage && index < (collectionScrollIndex + 1) * itemsPerPage;
                          const moviePoster = movie.poster_path ? `https://image.tmdb.org/t/p/w300${movie.poster_path}` : 'https://via.placeholder.com/300x450?text=No+Poster';
                          return (
                            <div key={movie.id} className="collection-item" onClick={() => handleMediaClick(movie, 'movie')} style={{
                              cursor: 'pointer', textAlign: 'center', position: 'relative',
                              display: visible ? 'block' : 'none', transition: 'transform 0.3s ease'
                            }}>
                              <img src={moviePoster} alt={movie.title} style={{ width: '100%', aspectRatio: '2/3', objectFit: 'cover', borderRadius: '8px', marginBottom: '10px', border: movie.id === currentMedia.id ? '3px solid #ec087aefdc' : 'none' }} />
                              <div style={{ fontWeight: 'bold', marginBottom: '5px', fontSize: window.innerWidth <= 768 ? '0.9rem' : '1rem' }}>{movie.title}</div>
                              <div style={{ color: '#aaa', fontSize: '0.9rem', marginBottom: '5px' }}>{movie.release_date ? new Date(movie.release_date).getFullYear() : 'N/A'}</div>
                              <div style={{ display: 'flex', justifyContent: 'center', gap: '3px', marginBottom: '5px' }}>{createStarRating(movie.vote_average, 'small')}</div>
                              {movie.id === currentMedia.id && (
                                <div style={{ position: 'absolute', top: '10px', background: 'linear-gradient(45deg, #1cb608e0, #037509dc)', color: 'white', borderRadius: '4px', padding: '4px 8px', fontSize: '0.8rem', fontWeight: 'bold', boxShadow: '0 8px 10px rgba(0,0,0,0.3)' }}>{t('youAreHere')}</div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      {canScrollRight && (
                        <button className="carousel-arrow right" onClick={() => scrollCollection('next')} style={{
                          position: 'absolute', right: '-20px', top: window.innerWidth <= 768 ? '80px' : '110px',
                          transform: 'translateY(-50%)', background: 'rgba(0,0,0,0.7)', border: 'none',
                          borderRadius: '50%', width: '40px', height: '40px', color: 'white',
                          cursor: 'pointer', zIndex: 5, display: 'flex', alignItems: 'center', justifyContent: 'center'
                        }}>
                          <FontAwesomeIcon icon={faChevronRight} />
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {/* === ACTION BUTTONS: MOVIES === */}
                {isMovie && renderActionButtons(true)}

                {/* === CAST SECTION (movies) === */}
                {isMovie && (
                  <>
                    {renderCast(credits?.cast, t('topCast'), topCastCount)}
                    {renderCast(credits?.cast?.slice(topCastCount), t('supportingCast'), supportingCastCount)}
                  </>
                )}

                {/* === ACTION BUTTONS: SERIES (after overview, before episodes) === */}
                {!isMovie && renderActionButtons(false)}

                {/* === SERIES EPISODES === */}
                {!isMovie && currentMedia.number_of_seasons > 0 && (
                  <div className="seasons-container" style={{ marginTop: '40px' }}>
                    <h3 className="seasons-title">{t('seasonsEpisodes')}</h3>
                    <div className="season-selector" style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '20px' }}>
                      <label className="season-label" htmlFor="seasonSelect">{t('selectSeason')}</label>
                      <select id="seasonSelect" className="season-select" value={selectedSeason} onChange={(e) => handleSeasonChange(parseInt(e.target.value))} style={{
                        background: 'rgb(27,27,27)', color: 'rgb(214,214,214)', border: '1px solid #333',
                        padding: '8px 15px', borderRadius: '5px'
                      }}>
                        {[...Array(currentMedia.number_of_seasons)].map((_, i) => (
                          <option key={i+1} value={i+1}>{t('seasonLabel')} {i+1}</option>
                        ))}
                      </select>
                    </div>
                    <div className="episodes-container" style={{ borderTop: '1px solid #333', paddingTop: '20px' }}>
                      {totalEpisodes > 0 ? (
                        <>
                          <h4 className="episode-list-title">{t('episodesOf', currentEpisodes.length, totalEpisodes)}</h4>
                          {currentEpisodes.map(episode => {
                            const stillPath = episode.still_path ? `https://image.tmdb.org/t/p/w500${episode.still_path}` : 'https://via.placeholder.com/500x280?text=No+Image';
                            const isUnreleased = !episode.still_path;
                            const isLast = lastWatchedEpisode === episode.id;
                            return (
                              <div key={episode.id} className={`episode-item ${selectedEpisodeId === episode.id ? 'selected' : ''}`} onClick={isUnreleased ? undefined : () => handleEpisodeClick(episode.id)} style={{ overflow: 'hidden', padding: '15px 0', borderBottom: '1px solid #222', transition: 'background 0.3s' }}>
                                <div className="episode-header" style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}>
                                  <div className="episode-number" style={{ fontSize: '24px', fontWeight: 'bold', color: '#59f001', minWidth: '40px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    {String(episode.episode_number).padStart(2, '0')}
                                    {isLast && <span className="last-watched-tag" style={{ color: '#f0f0f0', fontSize: '18px', padding: '4px 5px', borderRadius: '10px', fontWeight: '500' }}>{t('lastWatched')}</span>}
                                  </div>
                                </div>
                                {isUnreleased ? (
                                  <div className="unreleased-episode" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: window.innerWidth <= 768 ? '200px' : '150px', width: '100%', color: '#aaa', fontStyle: 'italic' }}>
                                    {t('episodeNotReleased')}
                                  </div>
                                ) : (
                                  <div className="episode-body" style={{ display: 'flex', flexDirection: window.innerWidth <= 768 ? 'column' : 'row', overflow: 'hidden' }}>
                                    <div className="episode-image-container" style={{ position: 'relative', width: window.innerWidth <= 768 ? '100%' : '35%', aspectRatio: '16/9', overflow: 'hidden', flexShrink: 0, borderRadius: '8px' }}>
                                      <img src={stillPath} alt={episode.name} className="episode-image" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                    </div>
                                    <div className="episode-content" style={{ width: window.innerWidth <= 768 ? '100%' : '65%', padding: window.innerWidth <= 768 ? '10px 0 0' : '0 0 0 15px' }}>
                                      <div className="episode-content-header" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                                        <h4 className="episode-title" style={{ margin: 0, fontSize: '18px', color: 'white' }}>{episode.name}</h4>
                                        <div className="episode-date" style={{ color: '#999' }}>{episode.air_date ? new Date(episode.air_date).toLocaleDateString() : 'TBA'}</div>
                                      </div>
                                      <div className={`episode-details ${selectedEpisodeId === episode.id ? 'hidden' : ''}`}>
                                        <p className="episode-overview" style={{ color: '#bbb', marginBottom: '10px', fontSize: '14px', lineHeight: '1.5' }}>{episode.overview ? episode.overview.substring(0, 100) + (episode.overview.length > 100 ? '...' : '') : t('noDescription')}</p>
                                        <div className="episode-meta" style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#aaa' }}>
                                          <div className="episode-runtime" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><FontAwesomeIcon icon={faClock} /><span>{episode.runtime || '?'} min</span></div>
                                          {episode.vote_average > 0 && <div className="episode-rating" style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#59f001' }}><FontAwesomeIcon icon={faStar} /><span>{episode.vote_average.toFixed(1)}</span></div>}
                                        </div>
                                      </div>
                                      {selectedEpisodeId === episode.id && (
                                        <div className={`episode-actions ${showEpisodeActions ? 'visible' : ''}`} style={{ display: 'flex', gap: '10px', marginTop: '15px', justifyContent: 'center' }}>
                                          <Link to={`/watch/${currentMedia.id}?s=${selectedSeason}&e=${episode.episode_number}`} className="action-btn1 action-btn1-pink" style={{ padding: '6px 33px', borderRadius: '8px', fontSize: '16px', fontWeight: '700', cursor: 'pointer', transition: 'all 0.3s ease', display: 'flex', alignItems: 'center', gap: '10px', textDecoration: 'none', background: 'linear-gradient(135deg, rgba(47,204,15,0.753), rgba(0,150,0,0.753))', color: 'white', border: '1px solid rgba(31,204,15,0)' }} onClick={(e) => { e.stopPropagation(); handleActionClick(episode.id); handlePlayClick(); }}>
                                            <FontAwesomeIcon icon={faPlay} />Play
                                          </Link>
                                          <Link to={`/alt-watch/${currentMedia.id}?s=${selectedSeason}&e=${episode.episode_number}`} className="action-btn1 action-btn1-gray" style={{ padding: '6px 33px', borderRadius: '8px', fontSize: '16px', fontWeight: '700', cursor: 'pointer', transition: 'all 0.3s ease', display: 'flex', alignItems: 'center', gap: '10px', textDecoration: 'none', background: 'rgba(255,255,255,0.1)', color: 'white', border: '1px solid rgba(255,255,255,0.2)' }} onClick={(e) => { e.stopPropagation(); handleActionClick(episode.id); handlePlayClick(); }}>
                                            <FontAwesomeIcon icon={faPlay} />Play2
                                          </Link>
                                          <Link to={`/alt-watch-two/${currentMedia.id}?s=${selectedSeason}&e=${episode.episode_number}`} className="action-btn1 action-btn1-gray" style={{ padding: '6px 33px', borderRadius: '8px', fontSize: '16px', fontWeight: '700', cursor: 'pointer', transition: 'all 0.3s ease', display: 'flex', alignItems: 'center', gap: '10px', textDecoration: 'none', background: 'rgba(255,255,255,0.1)', color: 'white', border: '1px solid rgba(255,255,255,0.2)' }} onClick={(e) => { e.stopPropagation(); handleActionClick(episode.id); handlePlayClick(); }}>
                                            <FontAwesomeIcon icon={faPlay} />Play3
                                          </Link>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                          {hasMoreEpisodes && (
                            <div className="load-more-container" style={{ display: 'flex', justifyContent: 'center', margin: '25px 0', width: '100%' }}>
                              <button className="load-more-btn" onClick={() => setEpisodePage(prev => prev + 1)} style={{
                                color: 'white', border: '1px solid rgba(255,255,255,0.3)', borderRadius: '30px',
                                padding: '12px 35px', fontSize: '1.05rem', fontWeight: '600', cursor: 'pointer',
                                transition: 'all 0.3s ease', display: 'flex', alignItems: 'center', background: 'transparent'
                              }}>
                                {t('loadMoreEpisodes', totalEpisodes - currentEpisodes.length)}
                              </button>
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="season-loading" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px', width: '100%', color: '#aaa', fontStyle: 'italic', marginTop: '60px', marginBottom: '20px', paddingBottom: '80px', borderBottom: '1px solid #333' }}>
                          <p>{t('seasonNotReleased')}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* === CAST SECTION (series) — moved below episodes === */}
                {!isMovie && (
                  <>
                    {renderCast(credits?.cast, t('topCast'), topCastCount)}
                    {renderCast(credits?.cast?.slice(topCastCount), t('supportingCast'), supportingCastCount)}
                  </>
                )}

                {/* === SIMILAR MOVIES (movies only) === */}
                {isMovie && recMovies.length > 0 && (
                  <div className="recommended-movies" style={{ padding: '20px 20px', borderTop: '1px solid rgba(255,255,255,0.1)', marginTop: 'auto' }}>
                    <h3 className="recommended-title">{t('similarMovies')}</h3>
                    <div className="recommended-grid" style={{
                      display: 'grid',
                      gridTemplateColumns: `repeat(${window.innerWidth <= 768 ? 2 : 5}, 1fr)`,
                      gap: '15px',
                      marginTop: '20px'
                    }}>
                      {recMovies.map(rec => {
                        const recPoster = rec.poster_path ? `https://image.tmdb.org/t/p/w500${rec.poster_path}` : 'https://via.placeholder.com/500x750?text=No+Poster';
                        return (
                          <div key={rec.id} className="recommended-movie" onClick={() => handleMediaClick(rec, 'movie')} style={{ cursor: isRateLimited ? 'not-allowed' : 'pointer', textAlign: 'center', opacity: isRateLimited ? 0.7 : 1 }}>
                            <img src={recPoster} alt={rec.title} style={{ width: '100%', aspectRatio: '2/3', objectFit: 'cover', borderRadius: '8px', marginBottom: '10px' }} />
                            <div className="recommended-movie-title" style={{ fontWeight: 'bold', marginBottom: '5px', fontSize: '0.9rem' }}>{rec.title}</div>
                            <div className="star-rating" style={{ display: 'flex', justifyContent: 'center', gap: '3px' }}>{createStarRating(rec.vote_average, 'small')}</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Trailer Modal */}
      {trailerKey && (
        <TrailerModal isOpen={showTrailerModal} onClose={() => setShowTrailerModal(false)} youtubeKey={trailerKey} />
      )}

      {/* Actor Modal */}
      {selectedActor && (
        <ActorModal
          actor={{
            id: selectedActor.id,
            name: selectedActor.name,
            imageUrl: selectedActor.profile_path ? `https://image.tmdb.org/t/p/original${selectedActor.profile_path}` : null,
          }}
          onClose={handleCloseActorModal}
          isMobile={window.innerWidth <= 768}
          onContentClick={handleFilmographyItemClick}
          history={localHistory}
          predefinedBackdrops={predefinedBackdrops}
          getHighResImage={getHighResImage}
          actorFallbackImage={actorFallbackImage}
          TMDB_API_KEY={apiKey}
          ratingFormat="stars"
        />
      )}
    </div>
  );
};

MediaModal.propTypes = {
  media: PropTypes.object,
  mediaType: PropTypes.oneOf(['movie', 'tv']),
  onClose: PropTypes.func.isRequired,
  onUpdate: PropTypes.func,
  onBack: PropTypes.func,
  onMediaChange: PropTypes.func,
  history: PropTypes.array
};

export default MediaModal;